import React, { useState, memo, useCallback } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './LocationList.module.scss';
import classNames from 'classnames/bind';
import BaseTable from '~/components/common/base-table/BaseTable';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import MenuOption from '../menu-option/MenuOption';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { ILocationCompany } from '~/utils/interface/location';
import { locationActions } from '~/thunks/location/locationSlice';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';

const cx = classNames.bind(styles);

type LocationListProps = {
  onOpenEdit: (id: string) => void;
};

function LocationList(props: LocationListProps) {
  const permisisons = usePermission();
  const { t } = useTranslation('admin/location');
  const headCells: Array<HeadCelType> = [
    {
      id: 'name',
      label: t(`name`),
      sortable: true,
      searchable: true,
    },
    {
      id: 'code',
      label: t(`code`),
      sortable: true,
      searchable: true,
    },

    {
      id: '1',
      label: '',
      sortable: false,
      searchable: false,
    },
  ];
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useAppDispatch();

  const { locationCompany, currentPage, totalPages, selectedIds, menuSelectedId } = useAppSelector(
    (state) => state.location
  );
  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    setAnchorEl(event.currentTarget);
    dispatch(locationActions.changeMenuSelectId(itemId));
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(locationActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (selectedIds !== undefined) {
      dispatch(locationActions.changeOpenAlertDelete(true));
    }
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      locationActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      locationActions.setFilter({
        page: newPage,
      })
    );
  };
  return (
    <Box className={cx('location-list-container')}>
      <BaseTable<ILocationCompany>
        data={locationCompany}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        page={currentPage}
        onRequestSort={handleRequestSort}
        totalPages={totalPages}
        renderCell={(locations: ILocationCompany) => {
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('name-container')}>
                  <Box className={cx('name')}>{locations.name}</Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box className={cx('assigness-container')}>
                  <Box className={cx('name')}>{locations.code}</Box>
                </Box>
              </TableCell>

              {(permisisons[PERMISSIONS.location_edit] || permisisons[PERMISSIONS.location_delete]) && (
                <TableCell>
                  <Box className={cx('menuoption-container')}>
                    <Box className='flex flex-1'></Box>
                    <IconButton
                      onClick={(event) => {
                        openOptionMenu(event, locations.id);
                      }}
                    >
                      <MenuVerticalIcon className='w-[19px] h-[19px]' />
                    </IconButton>
                  </Box>
                </TableCell>
              )}
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        enableEdit={permisisons[PERMISSIONS.location_edit]}
        enableDelete={permisisons[PERMISSIONS.location_delete]}
      />
    </Box>
  );
}

export default memo(LocationList);
